import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';
import { ReasonToken } from '@bonusly/recipient-entities';

import { Announcement, UserAnnouncements } from '../types/announcements';

export enum QueryKey {
	ActiveAnnouncements = 'active-announcements',
	AnnouncementsCreatedByUser = 'announcements-created-by-user',
	CurrentAnnouncements = 'current-announcements',
	PastAnnouncements = 'past-announcements',
	UserProfile = 'recognition-user',
}
interface CreateAnnouncementResponse {
	data: Announcement;
}
interface CreateAnnouncementParams {
	claimable_award_id?: string | null;
	description: string;
	email_notification: boolean;
	end_date: string;
	image?: File | null;
	reason_tokens?: ReasonToken[];
	send_slack_message: boolean;
	send_ms_teams_message: boolean;
	start_date: string;
	title: string;
}

interface DeleteAnnouncementResponse {
	data: Announcement;
}
interface DeleteAnnouncementParams {
	id: string;
}

interface GetAnnouncementsResponse {
	result: UserAnnouncements;
	success: boolean;
}
interface GetCurrentAnnouncementsResponse {
	result: Announcement[];
	success: boolean;
}

interface GetUserDataResponse {
	display_name: string;
	profile_pic_url: string;
	username: string;
}

interface UpdateAnnouncementResponse {
	data: Announcement;
}
interface UpdateAnnouncementParams {
	id: string;
	claimable_award_id?: string | null;
	description: string;
	email_notification: boolean;
	end_date: string;
	image?: File | null;
	reason_tokens?: ReasonToken[];
	remove_image: boolean;
	send_slack_message: boolean;
	send_ms_teams_message: boolean;
	start_date: string;
	title: string;
}

export const useCreateAnnouncement = (options = {}) => {
	const http = useHttpClient('v2');
	return useMutation(
		(params: CreateAnnouncementParams) => {
			const data = new FormData();
			if (params.image) {
				data.append('image', params.image);
			}
			if (params.claimable_award_id) {
				data.append('claimable_award_id', params.claimable_award_id);
			}
			data.append('description', params.description);
			data.append('email_notification', String(params.email_notification));
			data.append('end_date', params.end_date);
			data.append('send_slack_message', String(params.send_slack_message));
			data.append('send_ms_teams_message', String(params.send_ms_teams_message));
			data.append('start_date', params.start_date);
			data.append('title', params.title);

			if (params.reason_tokens) {
				data.append('reason_tokens', JSON.stringify(params.reason_tokens));
			}

			return http.post<CreateAnnouncementResponse>('/announcements/announcements', data);
		},
		{
			retry: false,
			...options,
		}
	);
};

export const useDeleteAnnouncement = (options = {}) => {
	const http = useHttpClient('v2');
	return useMutation(
		(params: DeleteAnnouncementParams) => {
			return http.delete<DeleteAnnouncementResponse>(`/announcements/announcements/${params.id}`);
		},
		{
			retry: false,
			...options,
		}
	);
};

export const useGetAnnouncementsCreatedByUser = () => {
	const http = useHttpClient('v2');

	return useQuery([QueryKey.AnnouncementsCreatedByUser], () => http.get<GetAnnouncementsResponse>('/announcements/announcements'), {
		retry: false,
		select: (data) => data.data.result,
	});
};

export const useGetCurrentAnnouncements = () => {
	const http = useHttpClient('v2');
	return useQuery(
		[QueryKey.CurrentAnnouncements],
		async () => {
			const currentAnnouncements = await http.get<GetCurrentAnnouncementsResponse>('/announcements/current_announcements');

			const announcementsData = currentAnnouncements.data?.result || [];

			const currentAnnouncementsWithUserData = announcementsData.map(async (announcement) => {
				const userData = await http.get<GetUserDataResponse>(`/user_management/users/${announcement.created_by_id}`);

				return {
					...announcement,
					created_by_display_name: userData.data.display_name || '',
					created_by_profile_pic_url: userData.data.profile_pic_url || '',
					created_by_username: userData.data.username || '',
				};
			});

			return Promise.all(currentAnnouncementsWithUserData);
		},
		{
			retry: false,
			select: (data: Announcement[]) => data.sort((a, b) => new Date(b.start_date).valueOf() - new Date(a.start_date).valueOf()),
		}
	);
};

export const useUpdateAnnouncement = (options = {}) => {
	const http = useHttpClient('v2');
	return useMutation(
		(params: UpdateAnnouncementParams) => {
			const data = new FormData();
			if (params.image) {
				data.append('image', params.image);
			}
			if (params.claimable_award_id) {
				data.append('claimable_award_id', params.claimable_award_id);
			}
			data.append('description', params.description);
			data.append('email_notification', String(params.email_notification));
			data.append('end_date', params.end_date);
			data.append('id', params.id);
			data.append('remove_image', String(params.remove_image));
			data.append('send_slack_message', String(params.send_slack_message));
			data.append('send_ms_teams_message', String(params.send_ms_teams_message));
			data.append('start_date', params.start_date);
			data.append('title', params.title);

			if (params.reason_tokens) {
				data.append('reason_tokens', JSON.stringify(params.reason_tokens));
			}

			return http.patch<UpdateAnnouncementResponse>(`/announcements/announcements/${params.id}`, data);
		},
		{
			retry: false,
			...options,
		}
	);
};

export const useInvalidateAnnouncementsQueries = () => {
	const queryClient = useQueryClient();
	return () => {
		queryClient.invalidateQueries([QueryKey.AnnouncementsCreatedByUser]);
		queryClient.invalidateQueries([QueryKey.CurrentAnnouncements]);
		queryClient.invalidateQueries([QueryKey.UserProfile]);
		queryClient.invalidateQueries([QueryKey.ActiveAnnouncements]);
		queryClient.invalidateQueries([QueryKey.PastAnnouncements]);
	};
};
